import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import WorkLogWidget from '../components/WorkLogWidget';
import TeamNowWidget from '../components/TeamNowWidget';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { 
  FolderKanban, Clock, AlertTriangle, PauseCircle, Users, 
  Bell, Calendar, ChevronRight, TrendingUp, X, RefreshCw, IndianRupee
} from 'lucide-react';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [teamWorkload, setTeamWorkload] = useState([]);
  const [teamOps, setTeamOps] = useState([]);
  const [salesDash, setSalesDash] = useState(null);
  const [salesLeaderboard, setSalesLeaderboard] = useState([]);
  const [accountTotals, setAccountTotals] = useState([]);
  const [myShoots, setMyShoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [showSidePanel, setShowSidePanel] = useState(false);

  const isManager = (user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r));
  const isSales = (user?.roles || [user?.role]).some(r => ['sales_person', 'admin', 'manager'].includes(r));
  const isShooter = (user?.roles || [user?.role]).some(r => ['photographer', 'videographer', 'editor_shooter', 'manager', 'admin'].includes(r));

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const promises = [
        api.get('/dashboard/kpis'),
        api.get('/projects')
      ];
      if (isSales) {
        promises.push(api.get('/sales/my-dashboard'));
      }
      const [kpisRes, projectsRes, salesRes] = await Promise.all(promises);
      setKpis(kpisRes.data);
      setRecentProjects(projectsRes.data.slice(0, 5));
      if (salesRes) setSalesDash(salesRes.data);

      // Upcoming shoots (all roles that may shoot)
      if (isShooter) {
        api.get('/shoots/my-upcoming').then(r => setMyShoots(r.data || [])).catch(() => {});
      }

      if (isManager) {
        const [workloadRes, opsRes, leaderboardRes, acctRes] = await Promise.all([
          api.get('/dashboard/team-workload'),
          api.get('/attendance/team-operations').catch(() => ({ data: [] })),
          api.get('/accounts/by-sales-person').catch(() => ({ data: [] })),
          api.get('/accounts/by-account').catch(() => ({ data: [] })),
        ]);
        setTeamWorkload(workloadRes.data);
        setTeamOps(opsRes.data);
        setSalesLeaderboard(leaderboardRes.data || []);
        setAccountTotals(acctRes.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMemberClick = async (member) => {
    setSelectedMember(member);
    setShowSidePanel(true);
    try {
      const res = await api.get(`/dashboard/team-workload/${member.id}`);
      setMemberDetail(res.data);
    } catch (err) {
      console.error('Failed to fetch member details:', err);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      not_started: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
      in_progress: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      on_hold_shoot: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      on_hold_client: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      on_hold_raw_files: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      with_freelancer: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      freelancer_delivered: 'bg-amber-700/10 text-amber-600 border-amber-700/20',
      in_review: 'bg-teal-500/10 text-teal-400 border-teal-500/20',
      revision_requested: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
      delivered: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      overdue: 'bg-rose-500/10 text-rose-400 border-rose-500/20 badge-overdue'
    };
    return colors[status] || colors.not_started;
  };

  const getRiskColor = (risk) => {
    const colors = {
      low: 'bg-emerald-500',
      medium: 'bg-yellow-500',
      high: 'bg-amber-500',
      critical: 'bg-rose-500',
      overdue: 'bg-rose-600',
      completed: 'bg-emerald-600'
    };
    return colors[risk] || colors.low;
  };

  const getWorkloadColor = (status) => {
    const colors = {
      healthy: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
      busy: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
      overloaded: 'text-rose-400 bg-rose-500/10 border-rose-500/20'
    };
    return colors[status] || colors.healthy;
  };

  const formatStatus = (status) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Here's what's happening in your studio today
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button 
            variant="outline"
            size="sm"
            className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10"
            onClick={fetchDashboardData}
            data-testid="refresh-dashboard"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          <Button 
            variant="outline" 
            className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white"
            onClick={() => navigate('/projects')}
          data-testid="view-all-projects-btn"
        >
          <FolderKanban className="h-4 w-4 mr-2" />
          View All Projects
        </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="kpi-grid">
        <Card className="bg-[#121212] border-white/10 card-hover cursor-pointer" onClick={() => navigate('/projects')} data-testid="kpi-active">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded">
                <FolderKanban className="h-4 w-4 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-white">{kpis?.active_projects || 0}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border-white/10 card-hover cursor-pointer" onClick={() => navigate('/projects?filter=overdue')} data-testid="kpi-overdue">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-500/10 rounded">
                <AlertTriangle className="h-4 w-4 text-rose-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-rose-400">{kpis?.overdue_projects || 0}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border-white/10 card-hover cursor-pointer" onClick={() => navigate('/projects?filter=on_hold')} data-testid="kpi-onhold">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded">
                <PauseCircle className="h-4 w-4 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-amber-400">{kpis?.on_hold_projects || 0}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">On Hold</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border-white/10 card-hover cursor-pointer" onClick={() => navigate('/projects?filter=due_week')} data-testid="kpi-dueweek">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-500/10 rounded">
                <Clock className="h-4 w-4 text-teal-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-white">{kpis?.due_this_week || 0}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Due Week</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border-white/10 card-hover cursor-pointer" onClick={() => navigate(isManager ? '/freelancers' : '/projects')} data-testid="kpi-freelancer">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded">
                <Users className="h-4 w-4 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-purple-400">{kpis?.with_freelancer || 0}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Freelancer</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {isSales && (
          <>
            <Card className="bg-[#121212] border-white/10 card-hover cursor-pointer" onClick={() => navigate('/leads')} data-testid="kpi-newleads">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500/10 rounded">
                    <TrendingUp className="h-4 w-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-emerald-400">{kpis?.new_leads_today || 0}</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">New Leads</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#121212] border-white/10 card-hover cursor-pointer" onClick={() => navigate('/leads')} data-testid="kpi-followups">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#F5A623]/10 rounded">
                    <Calendar className="h-4 w-4 text-[#F5A623]" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-[#F5A623]">{kpis?.follow_ups_today || 0}</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Follow-ups</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* In Studio Right Now — live team status, everyone */}
      <TeamNowWidget />

      {/* Daily Work Log — for everyone */}
      <WorkLogWidget />

      {/* Your Next Shoots — for photographers/editors/managers */}
      {isShooter && myShoots.length > 0 && (
        <Card className="bg-[#121212] border-white/10" data-testid="my-shoots-widget">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Calendar className="h-4 w-4 text-[#F5A623]" />
                Your Next Shoots
                <Badge variant="outline" className="ml-1 text-xs bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20">{myShoots.length}</Badge>
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/my-shoots')}
                className="text-zinc-400 hover:text-white text-xs">View All</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {myShoots.slice(0, 6).map(s => {
                const d = new Date(s.date);
                const isToday = new Date().toDateString() === d.toDateString();
                const daysLeft = Math.max(0, Math.ceil((d - new Date()) / 86400000));
                return (
                  <div key={`${s.project_id}-${s.event_id}`}
                    onClick={() => navigate(`/projects/${s.project_id}`)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      isToday ? 'bg-[#F5A623]/10 border-[#F5A623]/40' : 'bg-[#0A0A0A] border-white/5 hover:border-white/20'
                    }`}
                    data-testid={`shoot-card-${s.event_id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <Badge variant="outline" className={`text-[10px] capitalize ${
                        s.timing === 'night' ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                      }`}>{s.timing} shoot</Badge>
                      <span className={`text-[10px] font-semibold ${isToday ? 'text-[#F5A623]' : 'text-zinc-400'}`}>
                        {isToday ? 'TODAY' : daysLeft === 1 ? 'Tomorrow' : `${daysLeft}d left`}
                      </span>
                    </div>
                    <p className="text-white text-sm font-medium truncate">{s.event_name}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{s.client_name} · {s.project_title}</p>
                    <p className="text-[10px] text-zinc-500 mt-1.5">
                      {d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}
                    </p>
                    {(s.teammates?.length > 0 || s.freelancers?.length > 0) && (
                      <p className="text-[10px] text-emerald-400/80 mt-1 truncate">
                        with: {[...(s.teammates || []), ...(s.freelancers || [])].join(', ')}
                      </p>
                    )}
                    {s.as_freelancer && (
                      <Badge variant="outline" className="mt-1 text-[9px] bg-amber-500/10 text-amber-400 border-amber-500/20">Freelancer</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sales Person Quick Dashboard */}
      {isSales && salesDash && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="sales-dashboard-cards">
          <Card className="bg-[#121212] border-amber-500/20 card-hover cursor-pointer" onClick={() => navigate('/leads')}>
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Follow-ups Today</p>
              <p className={`text-2xl font-semibold ${salesDash.follow_ups_today > 0 ? 'text-amber-400' : 'text-white'}`}>{salesDash.follow_ups_today}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-blue-500/20 card-hover cursor-pointer" onClick={() => navigate('/leads')}>
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">New Leads</p>
              <p className="text-2xl font-semibold text-blue-400">{salesDash.new_leads}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-rose-500/20 card-hover cursor-pointer" onClick={() => navigate('/leads')}>
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Going Cold</p>
              <p className={`text-2xl font-semibold ${salesDash.cold_leads > 0 ? 'text-rose-400' : 'text-white'}`}>{salesDash.cold_leads}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-emerald-500/20 card-hover cursor-pointer" onClick={() => navigate('/sales-reports')}>
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">My Conversion</p>
              <p className="text-2xl font-semibold text-emerald-400">{salesDash.my_conversion_rate}%</p>
              <p className="text-xs text-zinc-600">{salesDash.booked_this_month} booked this month</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Operations Dashboard - Today's Team Status */}
      {isManager && teamOps.length > 0 && (
        <Card className="bg-[#121212] border-white/10" data-testid="operations-dashboard">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold">Today's Team Operations</h3>
              <div className="flex gap-3 text-xs">
                <span className="text-emerald-400">{teamOps.filter(t => t.status !== 'absent' && t.late_status === 'on_time').length} On Time</span>
                <span className="text-amber-400">{teamOps.filter(t => t.late_status === 'late').length} Late</span>
                <span className="text-rose-400">{teamOps.filter(t => t.late_status === 'half_day').length} Half Day</span>
                <span className="text-blue-400">{teamOps.filter(t => t.status === 'on_shoot').length} On Shoot</span>
                <span className="text-zinc-500">{teamOps.filter(t => t.status === 'absent').length} Not marked</span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
              {teamOps.map(member => {
                const statusColors = {
                  present: 'border-emerald-500/30 bg-emerald-500/5',
                  on_shoot: 'border-blue-500/30 bg-blue-500/5',
                  wfh: 'border-purple-500/30 bg-purple-500/5',
                  leave: 'border-rose-500/30 bg-rose-500/5',
                  absent: 'border-zinc-500/20 bg-zinc-500/5',
                };
                const lateColors = {
                  on_time: 'text-emerald-400',
                  late: 'text-amber-400',
                  half_day: 'text-rose-400',
                };
                const statusLabel = member.status === 'absent' ? 'Not marked' :
                  member.status === 'on_shoot' ? 'On Shoot' :
                  member.status === 'wfh' ? 'WFH' :
                  member.status === 'leave' ? 'Leave' : 'Present';

                return (
                  <div key={member.user_id} className={`p-2.5 rounded-lg border ${statusColors[member.status] || statusColors.absent}`}>
                    <p className="text-white text-xs font-medium truncate">{member.name}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-zinc-400">{statusLabel}</span>
                      {member.status !== 'absent' && member.mode === 'office' && (
                        <span className={`text-[10px] font-medium ${lateColors[member.late_status] || 'text-zinc-500'}`}>
                          {member.late_status === 'late' ? 'LATE' : member.late_status === 'half_day' ? 'HALF' : ''}
                        </span>
                      )}
                    </div>
                    {member.check_in_time && (
                      <p className="text-[10px] text-zinc-500 mt-0.5">In: {member.check_in_time}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sales Leaderboard + By Account (Managers only) */}
      {isManager && (salesLeaderboard.length > 0 || accountTotals.length > 0) && (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="sales-overview">
          {/* Sales Leaderboard */}
          {salesLeaderboard.length > 0 && (
            <Card className="bg-[#121212] border-white/10 lg:col-span-2">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-emerald-400" />
                    Sales Leaderboard
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/sales-reports')}
                    className="text-zinc-400 hover:text-white text-xs">See All</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {salesLeaderboard.slice(0, 5).map((sp, idx) => (
                    <div key={sp.user_id}
                      className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg border border-white/5 hover:border-white/10 transition-colors"
                      data-testid={`leaderboard-${sp.name}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                          idx === 0 ? 'bg-[#F5A623]/20 text-[#F5A623]' :
                          idx === 1 ? 'bg-zinc-400/20 text-zinc-300' :
                          idx === 2 ? 'bg-orange-700/20 text-orange-500' :
                          'bg-white/5 text-zinc-500'
                        }`}>{idx + 1}</div>
                        <div>
                          <p className="text-white text-sm font-medium">{sp.name}</p>
                          <p className="text-[11px] text-zinc-500">
                            {sp.bookings} booking{sp.bookings !== 1 ? 's' : ''} · {sp.leads_total} lead{sp.leads_total !== 1 ? 's' : ''} · {sp.conversion_rate}% conv.
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 text-sm font-semibold">&#8377;{(sp.booked_amount || 0).toLocaleString('en-IN')}</p>
                        <p className="text-[10px] text-zinc-500">booked</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* By Account */}
          {accountTotals.length > 0 && (
            <Card className="bg-[#121212] border-white/10">
              <CardHeader className="pb-4">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <IndianRupee className="h-4 w-4 text-[#F5A623]" />
                  By Account
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {accountTotals.filter(a => a.account !== 'Unassigned' || a.total > 0).map(a => (
                    <div key={a.account}
                      onClick={() => navigate('/accounts')}
                      className={`p-3 rounded-lg border cursor-pointer hover:border-[#F5A623]/40 transition-colors ${
                        a.account === 'Unassigned' ? 'bg-[#0A0A0A] border-white/5' : 'bg-[#F5A623]/5 border-[#F5A623]/10'
                      }`}
                      data-testid={`dash-account-${a.account}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${a.account === 'Unassigned' ? 'text-zinc-400' : 'text-[#F5A623]'}`}>{a.account}</span>
                        <span className="text-white text-sm font-semibold">&#8377;{(a.total || 0).toLocaleString('en-IN')}</span>
                      </div>
                      <p className="text-[10px] text-zinc-500 mt-0.5">{a.count} payment{a.count !== 1 ? 's' : ''}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Projects */}
        <div className="lg:col-span-2">
          <Card className="bg-[#121212] border-white/10">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg">Recent Projects</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-zinc-400 hover:text-white hover:bg-white/5" 
                  onClick={() => navigate('/projects')}
                  data-testid="see-all-projects-btn"
                >
                  See All <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentProjects.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <FolderKanban className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No projects yet</p>
                </div>
              ) : (
                recentProjects.map((project) => (
                  <div 
                    key={project.id} 
                    className="p-4 bg-[#0A0A0A] border border-white/5 rounded hover:border-white/10 transition-colors cursor-pointer"
                    onClick={() => navigate('/projects')}
                    data-testid={`project-card-${project.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-medium truncate">{project.title}</h3>
                          <Badge variant="outline" className={`text-xs ${getStatusColor(project.status)}`}>
                            {formatStatus(project.status)}
                          </Badge>
                        </div>
                        <p className="text-sm text-zinc-500">{project.client_name} • {project.event_type}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm text-zinc-400">
                          {project.days_remaining > 0 ? `${project.days_remaining}d left` : 
                           project.days_remaining === 0 ? 'Due today' : 
                           `${Math.abs(project.days_remaining)}d overdue`}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-zinc-500">{project.progress_percent}% timeline elapsed</span>
                        <span className={`text-xs ${getRiskColor(project.risk_level).replace('bg-', 'text-')}`}>
                          {project.risk_level.toUpperCase()}
                        </span>
                      </div>
                      <Progress 
                        value={project.progress_percent} 
                        className="h-1 bg-white/5"
                      />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Team Workload (Managers/Admins only) */}
        {isManager && (
          <div>
            <Card className="bg-[#121212] border-white/10">
              <CardHeader className="pb-4">
                <CardTitle className="text-white text-lg">Team Workload</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {teamWorkload.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No team members</p>
                  </div>
                ) : (
                  teamWorkload.map((member) => (
                    <div 
                      key={member.id} 
                      className="p-3 bg-[#0A0A0A] border border-white/5 rounded cursor-pointer hover:border-white/10 transition-colors"
                      onClick={() => handleMemberClick(member)}
                      data-testid={`team-member-${member.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-white font-medium">{member.name}</p>
                          <p className="text-xs text-zinc-500 capitalize">{(member.roles || [member.role]).map(r => r.replace(/_/g, ' ')).join(', ')}</p>
                        </div>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${getWorkloadColor(member.workload_status)}`}
                        >
                          {member.workload_status}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div className="p-2 bg-[#121212] rounded">
                          <p className="text-lg font-semibold text-white">{member.active_projects}</p>
                          <p className="text-[10px] text-zinc-500">Projects</p>
                        </div>
                        <div className="p-2 bg-[#121212] rounded">
                          <p className="text-lg font-semibold text-white">{member.tasks_due_today}</p>
                          <p className="text-[10px] text-zinc-500">Due Today</p>
                        </div>
                        <div className="p-2 bg-[#121212] rounded">
                          <p className="text-lg font-semibold text-amber-400">{member.on_hold_projects}</p>
                          <p className="text-[10px] text-zinc-500">On Hold</p>
                        </div>
                        <div className="p-2 bg-[#121212] rounded">
                          <p className="text-lg font-semibold text-white">{member.hours_this_week}h</p>
                          <p className="text-[10px] text-zinc-500">Week</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Actions for non-managers */}
        {!isManager && (
          <div>
            <Card className="bg-[#121212] border-white/10">
              <CardHeader className="pb-4">
                <CardTitle className="text-white text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white"
                  onClick={() => navigate('/tasks')}
                  data-testid="quick-action-tasks"
                >
                  <Clock className="h-4 w-4 mr-2" /> View My Tasks
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white"
                  onClick={() => navigate('/attendance')}
                  data-testid="quick-action-attendance"
                >
                  <Calendar className="h-4 w-4 mr-2" /> Mark Attendance
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white"
                  onClick={() => navigate('/time-tracking')}
                  data-testid="quick-action-time"
                >
                  <Clock className="h-4 w-4 mr-2" /> Log Time
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Team Member Side Panel */}
      <Sheet open={showSidePanel} onOpenChange={setShowSidePanel}>
        <SheetContent className="bg-[#121212] border-l border-white/10 w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white">
              {selectedMember?.name}'s Workload
            </SheetTitle>
          </SheetHeader>
          
          {memberDetail ? (
            <div className="mt-6 space-y-6">
              {/* Member Info */}
              <div className="p-4 bg-[#0A0A0A] rounded border border-white/5">
                <p className="text-white font-medium">{memberDetail.member.name}</p>
                <p className="text-sm text-zinc-500">{memberDetail.member.email}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(memberDetail.member.roles || [memberDetail.member.role]).map(r => (
                    <Badge key={r} variant="outline" className="capitalize bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20">
                      {r.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Projects */}
              <div>
                <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3">
                  Active Projects ({memberDetail.projects.length})
                </h3>
                <div className="space-y-2">
                  {memberDetail.projects.map(project => (
                    <div key={project.id} className="p-3 bg-[#0A0A0A] rounded border border-white/5">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-white text-sm font-medium">{project.title}</p>
                          <p className="text-xs text-zinc-500">{project.client_name}</p>
                        </div>
                        <Badge variant="outline" className={`text-xs ${getStatusColor(project.status)}`}>
                          {formatStatus(project.status)}
                        </Badge>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <span className="text-zinc-500">Deadline: {formatDate(project.deadline)}</span>
                        <span className={`${getRiskColor(project.risk_level).replace('bg-', 'text-')}`}>
                          {project.risk_level.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                  {memberDetail.projects.length === 0 && (
                    <p className="text-sm text-zinc-500 text-center py-4">No active projects</p>
                  )}
                </div>
              </div>
              
              {/* Tasks */}
              <div>
                <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3">
                  Pending Tasks ({memberDetail.tasks.length})
                </h3>
                <div className="space-y-2">
                  {memberDetail.tasks.map(task => (
                    <div key={task.id} className="p-3 bg-[#0A0A0A] rounded border border-white/5">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-white text-sm">{task.title}</p>
                          <p className="text-xs text-zinc-500">{task.project_title}</p>
                        </div>
                        <Badge variant="outline" className={`text-xs ${
                          task.priority === 'high' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' :
                          task.priority === 'medium' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                          'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                        }`}>
                          {task.priority}
                        </Badge>
                      </div>
                      {task.due_date && (
                        <p className="text-xs text-zinc-500 mt-1">Due: {formatDate(task.due_date)}</p>
                      )}
                    </div>
                  ))}
                  {memberDetail.tasks.length === 0 && (
                    <p className="text-sm text-zinc-500 text-center py-4">No pending tasks</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default DashboardPage;
